/**
 * /login — one route, three experiences, chosen by the host:
 *
 *   1. CENTRAL auth origin (`login[-staging].3mote.com`, the one origin
 *      Google, Microsoft + Apple accept): `<CentralLoginPage>` — password + inline
 *      SSO, then the workspace resolver hands off to a tenant subdomain
 *      (`?return=` names it, `?next=` carries a guarded deep-link path).
 *   2. DEPLOYED tenant host (`aeeiee[-staging].3mote.com`):
 *      `<TenantLoginRedirect>` — sign-in no longer happens here. A
 *      logged-out visit hard-replaces to the central origin; a signed-in
 *      one goes home. The `.3mote.com` session cookies make the central
 *      sign-in valid on this host.
 *   3. LOCALHOST / unknown host (no central twin — `buildCentralLoginUrl`
 *      returns null): the inline `<LoginPage>` below. `.3mote.com`
 *      cookies can't reach localhost, so the central hop would strand
 *      the session; dev keeps the full local form incl. the inline SSO
 *      buttons (GIS iframe + MSAL popup when their env vars are set).
 *
 * The inline page signs in against Cognito, bootstraps the BE user row,
 * then redirects to `/`. Two recovery paths gated by Cognito error codes:
 *
 *   - UserNotConfirmedException — the user signed up but never typed the
 *     email-verification code. Show an inline "Email not verified →
 *     Resend code" banner; clicking swaps the form for the shared
 *     `<VerifyCodePanel>` (6-digit code → confirm → sign in → bootstrap →
 *     redirect).
 *   - NotAuthorizedException with "password attempts exceeded" /
 *     "attempt limit exceeded" / "too many failed attempts" — the account
 *     is locked. Distinct banner with a link to /forgot-password
 *     (`isAccountLockedError` sniffs the message text).
 *
 * 1:1 port of the legacy `Login.jsx` lockout banner copy
 * (3mote-fe/src/pages/auth/Login/Login.jsx) and the legacy /signup
 * verify step.
 */

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { adoptExternalSession, signIn } from '@/auth/cognito'
import { isCentralAuthHost } from '@/auth/host-parser'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { CentralLoginPage } from '@/features/auth/CentralLoginPage'
import { SsoSection } from '@/features/auth/SsoSection'
import { TenantLoginRedirect } from '@/features/auth/TenantLoginRedirect'
import { LegalLinks } from '@/features/auth/TermsNotice'
import { VerifyCodePanel } from '@/features/auth/VerifyCodePanel'
import { buildCentralLoginUrl } from '@/features/auth/central-redirect'
import type { AuthBanner } from '@/features/auth/login-banners'
import { describeSignInError } from '@/features/auth/login-banners'
import { useBootstrap } from '@/features/auth/use-bootstrap'
import { useResendCode } from '@/features/auth/use-resend-code'
import type { SsoSignInResponse } from '@/features/auth/use-sso'

interface LoginSearch {
  /**
   * Central-origin only: the tenant subdomain that sent the user here.
   * Used as a lookup key into the authenticated membership list — never
   * composed into a redirect directly (open-redirect guard lives in
   * `features/auth/central-redirect.ts`).
   */
  return?: string
  /**
   * Central-origin only: the same-origin path the visitor was on before
   * the tenant host bounced them here (`/board/…?selectedDeliverable=…`),
   * so the deep link survives the hop. Passed through as-is; it is
   * sanitized where it is COMPOSED into a URL (`composeWorkspaceUrl`),
   * never trusted here.
   */
  next?: string
}

export const Route = createFileRoute('/login')({
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    return: typeof search.return === 'string' ? search.return : undefined,
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
})

function LoginRoute() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const goHome = useCallback(() => {
    void navigate({ to: '/', replace: true })
  }, [navigate])

  const host = typeof window === 'undefined' ? '' : window.location.host
  if (isCentralAuthHost(host)) {
    return <CentralLoginPage returnParam={search.return} nextParam={search.next} />
  }
  // No `next` here: by the time a tenant `/login` visit happens the deep
  // link is already gone (the root guard carried it when it bounced the
  // original path) — there is nothing left to preserve.
  const centralUrl = buildCentralLoginUrl(host)
  if (centralUrl !== null) {
    return <TenantLoginRedirect centralUrl={centralUrl} onSignedIn={goHome} />
  }
  return <LoginPage />
}

export function LoginPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const bootstrap = useBootstrap()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [banner, setBanner] = useState<AuthBanner>(null)
  const [submitting, setSubmitting] = useState(false)
  const [ssoSubmitting, setSsoSubmitting] = useState(false)

  // Verify-code sub-flow. `<VerifyCodePanel>` mounts when banner.kind ===
  // 'unconfirmed' and the user clicked "Resend code" (which moves us into
  // `verifying`); the panel owns the code / error state itself.
  const [verifying, setVerifying] = useState(false)
  const [verifyDestination, setVerifyDestination] = useState<string | null>(null)
  const resendCode = useResendCode(email)

  const finalizeSignIn = async () => {
    // Best-effort bootstrap; the BE is idempotent. We pass the email
    // as a fallback name for accounts created via the legacy stack
    // that don't have a name attribute yet.
    try {
      await bootstrap.mutateAsync({ name: email })
    } catch {
      // Bootstrap failures shouldn't block login — the home page will
      // surface the underlying problem via /api/me.
    }
    navigate({ to: '/' })
  }

  /**
   * Shared SSO success path. The BE already resolved (and refused, if
   * ineligible) the user — adopt the Cognito bundle exactly like a
   * password session, then best-effort bootstrap (mirrors
   * `finalizeSignIn`: the BE is idempotent and a bootstrap failure
   * surfaces via /api/me on the home page, never blocks sign-in).
   */
  const finalizeSsoSignIn = async (res: SsoSignInResponse) => {
    adoptExternalSession(res.tokens)
    try {
      await bootstrap.mutateAsync({ name: res.user.name ?? res.user.email })
    } catch {
      // Swallowed on purpose — see finalizeSignIn above.
    }
    navigate({ to: '/' })
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setBanner(null)
    setSubmitting(true)
    try {
      await signIn(email, password)
      await finalizeSignIn()
    } catch (err) {
      setBanner(describeSignInError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    const destination = await resendCode.resend()
    if (destination !== null) {
      setVerifyDestination(destination)
      setVerifying(true)
    }
  }

  // ─── Verify-code panel (sub-flow) ───
  if (verifying && banner?.kind === 'unconfirmed') {
    return (
      <VerifyCodePanel
        email={email}
        password={password}
        destination={verifyDestination}
        onSignedIn={finalizeSignIn}
        onBack={() => {
          setVerifying(false)
          setBanner(null)
        }}
      />
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-semibold text-fg">{t('login.title')}</h1>
        <p className="mb-6 text-sm text-fg-muted">{t('login.subtitle')}</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField
            id="email"
            label={t('login.emailLabel')}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (banner) setBanner(null)
            }}
          />
          <FormField
            id="password"
            label={t('login.passwordLabel')}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (banner) setBanner(null)
            }}
          />

          {banner?.kind === 'locked' && (
            <div
              role="alert"
              className="flex flex-col gap-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700/60 dark:bg-amber-900/40"
            >
              <strong className="font-semibold text-amber-900 dark:text-amber-100">
                {t('login.lockedTitle')}
              </strong>
              <span className="text-amber-800 dark:text-amber-200">{banner.message}</span>
              <Link
                to="/forgot-password"
                className="mt-1 self-start font-medium text-amber-900 underline decoration-amber-400 underline-offset-2 hover:decoration-amber-900 dark:text-amber-100 dark:decoration-amber-500 dark:hover:decoration-amber-100"
              >
                {t('login.lockedResetLink')}
              </Link>
            </div>
          )}

          {banner?.kind === 'unconfirmed' && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm dark:border-sky-700/60 dark:bg-sky-900/40"
            >
              <strong className="font-semibold text-sky-900 dark:text-sky-100">
                {t('login.unconfirmedTitle')}
              </strong>
              <span className="text-sky-800 dark:text-sky-200">
                {t('login.unconfirmedMessage')}
              </span>
              <Button
                type="button"
                variant="secondary"
                disabled={resendCode.submitting || !email}
                onClick={() => void handleResendCode()}
                className="self-start"
              >
                {resendCode.submitting ? t('login.resendCodeSubmitting') : t('login.resendCode')}
              </Button>
              {resendCode.error && (
                <span className="text-red-700 dark:text-red-200">{resendCode.error}</span>
              )}
            </div>
          )}

          {banner?.kind === 'wrong-password' && (
            <p
              className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200"
              role="alert"
            >
              {banner.message}
            </p>
          )}
          {banner?.kind === 'generic' && (
            <p
              className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200"
              role="alert"
            >
              {banner.message}
            </p>
          )}

          <Button type="submit" disabled={submitting || ssoSubmitting} className="w-full">
            {submitting ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>

        <SsoSection
          disabled={submitting}
          onPendingChange={(pending) => {
            setSsoSubmitting(pending)
            if (pending) setBanner(null)
          }}
          onSignedIn={finalizeSsoSignIn}
        />

        <div className="mt-4 flex items-center justify-end text-sm">
          <Link className="text-fg-muted hover:text-fg" to="/forgot-password">
            {t('login.forgotPasswordLink')}
          </Link>
        </div>
        <div className="mt-6 border-t border-border pt-4">
          <p className="text-center text-xs text-fg-muted">
            {t('login.privateBeta')}{' '}
            <Link
              className="text-fg underline decoration-fg-subtle underline-offset-2 hover:decoration-fg"
              to="/signup"
            >
              {t('login.requestAccessLink')}
            </Link>
          </p>
          <LegalLinks className="mt-2" />
        </div>
      </div>
    </div>
  )
}
